import { Box, Card, CardContent, Grid, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'

import type { PlaceToVisit } from '@/src/types/destinationResult'

interface PlacesToVisitProps {
    places: PlaceToVisit[]
}

const CATEGORY_COLORS: Record<PlaceToVisit['category'], string> = {
    landmark: 'primary.main',
    religious: 'secondary.main',
    museum: 'info.main',
    shopping: 'warning.main',
    nature: 'success.main',
    district: 'text.secondary',
} as const

const PlacesToVisit: React.FC<PlacesToVisitProps> = ({ places }) => {
    const t = useTranslations('destinationPage')
    return (
        <Box component="section" sx={{ mb: 4 }}>
            <Typography
                component="h2"
                variant="h6"
                sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.1rem' }, mb: 2 }}
            >
                {t('placesToVisit')}
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 2 }} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {places.map((place, i) => (
                    <Grid key={`${place.name}-${i}`} size={{ sm: 12, md: 6 }} component="li">
                        <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                            <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } } }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                        {place.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.7rem',
                                            color: CATEGORY_COLORS[place.category],
                                            textTransform: 'capitalize',
                                            flexShrink: 0
                                        }}
                                    >
                                        {place.category}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}>
                                    {place.description}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    )
}

export default PlacesToVisit
export type { PlacesToVisitProps }
